<!-- Wrapper Component for the Photos Module -->
<template>
  <div
    class="bg-black flex-col rounded-lg flex"
    :class="[expanded ? '' : 'h-auto']"
  >
    <!-- Header -->
    <div
      class="flex justify-between items-center p-4 border-b border-neutral-800 flex-shrink-0"
    >
      <div class="flex items-center gap-4">
        <h1 class="text-lg font-bold text-white">PHOTOS</h1>
        <Button
          v-if="expanded"
          @click="showHelpModal = true"
          variant="secondary"
          icon="help-circle"
          size="sm"
          rounded="md"
          class="hover:text-[#27bdf4] transition-colors"
        >
          Help
        </Button>
      </div>
      <button
        @click="toggleExpanded"
        class="text-white hover:text-[#27bdf4] transition-colors"
      >
        <i
          class="mdi"
          :class="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        ></i>
      </button>
    </div>
    <!-- PLAYBACK & FILTERING -->
    <div
      v-if="expanded"
      class="flex flex-col h-[calc(100svh-200px)] lg:flex-row p-4 gap-4 overflow-hidden"
    >
      <!-- PLAYBACK -->
      <div
        class="flex w-full lg:w-4/6 xl:w-9/12 rounded-lg bg-neutral-800 overflow-hidden flex-shrink-0 flex-col"
      >
        <!-- Sequence Viewer Wrapper -->
        <div class="flex flex-col w-full h-full">
          <!-- Image Viewer -->
          <div class="w-full flex-1 relative bg-neutral-900 min-h-0">
            <!-- Regular Image Display -->
            <div v-if="!comparisonMode" class="h-full">
              <img
                v-if="currentImage && !loading"
                :src="currentImage.url"
                :alt="imageAltText"
                class="absolute inset-0 w-full h-full object-contain"
              />
            </div>

            <!-- Comparison Mode -->
            <div v-else class="h-full relative" id="comparison">
              <!-- Base Container -->
              <div class="absolute inset-0">
                <!-- Previous Image (Base Layer) -->
                <img
                  v-if="currentImage && previousImage && !loading"
                  :src="previousImage.url"
                  :alt="previousImageTimestamp"
                  class="w-full h-full object-contain"
                />

                <!-- Current Image (Overlay Layer) with Clip -->
                <div
                  v-if="currentImage && previousImage && !loading"
                  class="absolute inset-0"
                  :style="comparisonClipStyle"
                >
                  <img
                    :src="currentImage.url"
                    :alt="currentImageTimestamp"
                    class="w-full h-full object-contain"
                  />
                </div>

                <!-- Timestamps -->
                <div
                  class="absolute bottom-4 left-4 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded"
                >
                  {{
                    formatDate(
                      currentImage?.userTimestamp || currentImage?.timestamp
                    )
                  }}
                </div>
                <div
                  class="absolute bottom-4 right-4 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded"
                >
                  {{
                    formatDate(
                      previousImage?.userTimestamp || previousImage?.timestamp
                    )
                  }}
                </div>

                <!-- Comparison Line -->
                <div
                  class="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10 cursor-col-resize"
                  :style="comparisonLineStyle"
                  @mousedown.prevent="startComparisonDrag"
                >
                  <div
                    class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-neutral-800 border-2 border-white flex items-center justify-center cursor-col-resize"
                  >
                    <i class="mdi mdi-arrow-left-right text-white text-lg"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div
              v-if="loading"
              class="absolute inset-0 flex items-center justify-center"
            >
              <span class="text-white">Loading...</span>
            </div>

            <!-- No Images State -->
            <div
              v-if="!loading && (!images || images.length === 0)"
              class="absolute inset-0 flex items-center justify-center"
            >
              <p class="text-gray-500">No images available</p>
            </div>
          </div>
          <!-- Playback -->
          <div
            class="flex w-full p-2 gap-8 items-center border-t border-neutral-700 flex-shrink-0"
          >
            <!-- Playback Controls -->
            <div class="flex items-center space-x-3 px-1">
              <Button
                @click="handleFastForward"
                variant="secondary"
                icon="rewind"
                size="sm"
                rounded="md"
                class="hover:text-[#27bdf4] transition-colors"
              />

              <Button
                @click="togglePlayback"
                variant="secondary"
                :icon="isPlaying ? 'pause' : 'play'"
                size="sm"
                rounded="md"
                class="hover:text-[#27bdf4] transition-colors"
              />

              <Button
                @click="handleRewind"
                variant="secondary"
                icon="fast-forward"
                size="sm"
                rounded="md"
                class="hover:text-[#27bdf4] transition-colors"
              />

              <Button
                @click="toggleLoop"
                variant="secondary"
                icon="repeat"
                size="sm"
                rounded="md"
                :class="[
                  'transition-colors',
                  loop
                    ? '!bg-[#27bdf4] text-white hover:!bg-[#1da8dc]'
                    : 'hover:text-[#27bdf4]',
                ]"
              />

              <Button
                @click="toggleComparisonMode"
                variant="secondary"
                icon="compare"
                size="sm"
                rounded="md"
                :class="[
                  'transition-colors',
                  comparisonMode
                    ? '!bg-[#27bdf4] text-white hover:!bg-[#1da8dc]'
                    : 'hover:text-[#27bdf4]',
                ]"
              />
            </div>
            <!-- Timeline Slider -->
            <div class="flex-1">
              <!-- Timeline Bar -->
              <div class="relative h-12" ref="timeline">
                <!-- Background Track -->
                <div
                  class="absolute inset-x-0 top-1/2 h-0.5 -mt-[1px] bg-neutral-700 rounded"
                ></div>

                <!-- Progress Bar -->
                <div
                  class="absolute top-1/2 h-0.5 -mt-[1px] bg-[#27bdf4] rounded-l"
                  :style="{
                    width: timelineService.getTimelineStyle(
                      currentImage,
                      images
                    ).width,
                    left: timelineService.getTimelineStyle(currentImage, images)
                      .left,
                  }"
                ></div>

                <!-- Time Markers -->
                <div
                  v-for="(marker, index) in timelineService.getTimeMarkers(
                    images
                  )"
                  :key="index"
                  class="absolute top-1/2 -mt-1 w-[2px] h-2 bg-neutral-600"
                  :style="{ left: `${marker.position}%` }"
                  :title="marker.date"
                ></div>

                <!-- Timeline Track - Make it clickable -->
                <div
                  class="absolute inset-x-0 top-1/2 h-4 -mt-2 cursor-pointer"
                  @click="handleTimelineClick"
                ></div>

                <!-- Start Handle -->
                <div
                  class="absolute top-1/2 -mt-1.5 w-3 h-3 bg-neutral-800 rounded-full cursor-pointer shadow-lg transform transition-all duration-200 hover:scale-150 hover:bg-[#27bdf4] group"
                  :style="{
                    left: `calc(${timelineService.rangeStart}% - 6px)`,
                  }"
                  @mousedown="startDragging('start', $event)"
                >
                  <div
                    class="absolute inset-0 flex items-center justify-center"
                  >
                    <div
                      class="w-1 h-1 bg-white rounded-full opacity-75 group-hover:opacity-100"
                    ></div>
                  </div>
                </div>

                <!-- End Handle -->
                <div
                  class="absolute top-1/2 -mt-1.5 w-3 h-3 bg-neutral-800 rounded-full cursor-pointer shadow-lg transform transition-all duration-200 hover:scale-150 hover:bg-[#27bdf4] group"
                  :style="{ left: `calc(${timelineService.rangeEnd}% - 6px)` }"
                  @mousedown="startDragging('end', $event)"
                >
                  <div
                    class="absolute inset-0 flex items-center justify-center"
                  >
                    <div
                      class="w-1 h-1 bg-white rounded-full opacity-75 group-hover:opacity-100"
                    ></div>
                  </div>
                </div>

                <!-- Active Image Indicator -->
                <div
                  class="absolute top-1/2 -mt-2 w-4 h-4 bg-[#27bdf4] rounded-full shadow-lg transform -translate-x-1/2 cursor-grab active:cursor-grabbing transition-all duration-200 hover:scale-125 ring-2 ring-neutral-900"
                  :style="{
                    left: `${timelineService.getCurrentImagePosition(
                      currentImage,
                      images
                    )}%`,
                  }"
                  @mousedown.prevent="startImageDragging"
                >
                  <div
                    class="absolute inset-0 flex items-center justify-center"
                  >
                    <div class="w-1.5 h-1.5 bg-white rounded-full"></div>
                  </div>
                </div>

                <!-- Time Labels -->
                <div
                  v-if="activeDragHandle === 'start'"
                  class="absolute -top-4 text-xs text-neutral-400 font-medium bg-neutral-900 border border-neutral-700 px-2 py-1 rounded-md shadow-lg transition-opacity duration-200 transform -translate-x-1/2 whitespace-nowrap"
                  :style="{
                    left: `calc(${timelineService.rangeStart}%)`,
                  }"
                >
                  {{ formatDate(timelineService.getRangeStartDate(images)) }}
                </div>
                <div
                  v-if="activeDragHandle === 'end'"
                  class="absolute -top-4 text-xs text-neutral-400 font-medium bg-neutral-900 border border-neutral-700 px-2 py-1 rounded-md shadow-lg transition-opacity duration-200 transform -translate-x-1/2 whitespace-nowrap"
                  :style="{
                    left: `calc(${timelineService.rangeEnd}%)`,
                  }"
                >
                  {{ formatDate(timelineService.getRangeEndDate(images)) }}
                </div>

                <!-- Current Time Label -->
                <div
                  v-if="isDraggingImage"
                  class="absolute -top-4 text-xs text-[#27bdf4] font-medium bg-neutral-900 border border-neutral-700 px-2 py-1 rounded-md shadow-lg transition-opacity duration-200 transform -translate-x-1/2 whitespace-nowrap"
                  :style="{
                    left: `${timelineService.getCurrentImagePosition(
                      currentImage,
                      images
                    )}%`,
                  }"
                >
                  {{
                    formatDate(
                      currentImage?.userTimestamp || currentImage?.timestamp
                    )
                  }}
                </div>
              </div>
            </div>
            <!-- Download Button Group -->
            <div class="relative">
              <Button
                @click="toggleDownloadMenu"
                variant="secondary"
                icon="download"
                size="sm"
                rounded="md"
                :disabled="isRenderingVideo || !currentImage"
              >
                <div
                  v-if="isRenderingVideo"
                  class="absolute inset-0 flex items-center justify-center"
                >
                  <div
                    class="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#27bdf4]"
                  ></div>
                </div>
              </Button>

              <!-- Download Options Dropdown -->
              <div
                v-if="showDownloadMenu"
                class="absolute right-0 bottom-full mb-2 bg-neutral-900 rounded-md shadow-lg z-10 border border-neutral-700"
              >
                <button
                  @click="downloadCurrentImage"
                  class="flex items-center w-full px-4 py-2 text-sm text-white hover:bg-neutral-800 whitespace-nowrap"
                  :disabled="!currentImage"
                >
                  <i class="mdi mdi-image mr-2"></i>
                  Download Current Image
                </button>
                <button
                  @click="openRenderModal"
                  class="flex items-center w-full px-4 py-2 text-sm text-white hover:bg-neutral-800 whitespace-nowrap"
                  :disabled="!images.length"
                >
                  <i class="mdi mdi-video mr-2"></i>
                  Download as Timelapse
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- FILTERING -->
      <div
        class="flex w-full lg:w-2/6 xl:w-3/12 flex-col rounded-lg bg-neutral-800 min-h-0"
      >
        <!-- Header & Sorting -->
        <div
          class="flex flex-col p-4 gap-2 border-b border-neutral-700 flex-shrink-0"
        >
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-black text-white">PHOTOS UPLOADED</h2>
            <button
              @click="toggleSortOrder"
              class="text-gray-400 hover:text-white"
            >
              <i
                class="mdi"
                :class="
                  sortOrder === 'desc'
                    ? 'mdi-sort-descending'
                    : 'mdi-sort-ascending'
                "
              ></i>
            </button>
          </div>
          <!-- Tag Dropdown & Upload Button -->
          <div
            class="flex flex-col lg:flex-row gap-2 justify-between items-center"
          >
            <select
              v-model="selectedTag"
              :disabled="!tags.length || isAnyFileUploading"
              class="flex-1 w-full h-full bg-neutral-900 border border-neutral-700 rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#27bdf4] disabled:opacity-50 disabled:cursor-not-allowed"
              @change="handleTagChange"
            >
              <option value="" disabled selected>Select a tag</option>
              <option
                v-for="tag in tagOptions"
                :key="tag.value"
                :value="tag.value"
              >
                {{ tag.label }}
              </option>
            </select>
            <Button
              variant="secondary"
              icon="cloud-upload"
              @click="openUploadModal"
              class="flex-shrink-0"
            >
              Upload Photo
            </Button>
          </div>
        </div>

        <!-- Image List -->
        <div class="flex-1 overflow-y-auto min-h-0">
          <div v-if="loading" class="text-center py-4 text-gray-400">
            Loading...
          </div>
          <div v-else-if="error" class="text-center py-4 text-red-500">
            {{ error }}
          </div>
          <div v-else-if="!selectedTag" class="text-center py-4 text-gray-400">
            Please select a tag to view images
          </div>
          <div v-else class="space-y-2 p-4">
            <div
              v-for="(image, index) in sortedImages"
              :key="image.id"
              class="w-full bg-neutral-900 rounded-lg p-2 flex items-center gap-2 cursor-pointer hover:bg-neutral-950 transition-colors"
              :class="getImageDisplayClass(image)"
              @click="handleImageClick(image)"
            >
              <img
                :src="image.url"
                :alt="image.originalName"
                class="w-16 h-12 object-cover rounded flex-shrink-0"
              />
              <div class="flex-1 min-w-0">
                <div class="text-white text-sm truncate">
                  {{ formatDate(image.userTimestamp || image.timestamp) }}
                </div>
                <div class="text-gray-400 text-xs truncate">
                  {{ image.originalName }}
                </div>
              </div>
              <div class="flex items-center flex-shrink-0">
                <div class="relative image-menu">
                  <button
                    @click.stop="toggleImageMenu(index)"
                    class="text-white hover:text-[#27bdf4] transition-colors p-1"
                  >
                    <i class="mdi mdi-dots-vertical"></i>
                  </button>
                  <!-- Dropdown Menu -->
                  <div
                    v-if="activeImageMenu === index"
                    class="absolute right-0 mt-2 w-48 bg-neutral-900 hover:bg-neutral-950 rounded-md shadow-lg z-10 border border-neutral-700 drop-shadow-lg"
                  >
                    <button
                      @click.stop="deleteImage(image)"
                      class="block w-full text-left px-4 py-2 text-sm text-red-500"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Delete Confirmation Modal -->
    <div
      v-if="showDeleteImageModal"
      class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]"
      @click="cancelDeleteImage"
    >
      <div class="bg-neutral-800 rounded-lg w-full max-w-md p-6" @click.stop>
        <h3 class="text-xl font-bold text-white mb-4">Delete Image</h3>
        <p class="text-gray-300 mb-6">
          Are you sure you want to delete this image? This action cannot be
          undone.
        </p>
        <div class="flex justify-end space-x-3">
          <Button @click="cancelDeleteImage" variant="neutral" size="md">
            Cancel
          </Button>
          <Button @click="confirmDeleteImage" variant="danger" size="md">
            Delete
          </Button>
        </div>
      </div>
    </div>

    <!-- Add UploadModal -->
    <UploadModal
      :is-open="showModal"
      :modal-type="modalType"
      :branch-id="branchId"
      :nursery-id="nurseryId"
      :tags="tags"
      :active-tag="selectedTag"
      @close="closeModal"
      @refresh="handleTagChange"
      @upload-success="handleUploadSuccess"
      @image-uploaded="handleImageUploaded"
      @new-tag="handleNewTag"
      @delete-tag="handleDeleteTag"
      @error="handleError"
    />

    <!-- Render Modal -->
    <RenderModal
      :is-open="showRenderModal"
      :render-progress="renderProgress"
      @close="closeRenderModal"
      @start-render="startRender"
    />

    <!-- Add HelpModal before the closing template tag -->
    <HelpModal :is-open="showHelpModal" @close="showHelpModal = false" />
  </div>
</template>

<script>
  import Button from "@/components/ui/Button.vue";
  import UploadModal from "./UploadModal.vue";
  import RenderModal from "./RenderModal.vue";
  import HelpModal from "./HelpModal.vue";
  import { TagManager, ImageManager } from "./services/GoogleCloudBucket";
  import { RenderService } from "./services/RenderService";
  import {
    formatDate,
    PlaybackController,
    ComparisonController,
    ImageDisplayManager,
    DisplayStateManager,
    TimelineService,
  } from "./services/PlaybackServices";

  export default {
    name: "PhotosModule",
    components: {
      Button,
      UploadModal,
      RenderModal,
      HelpModal,
    },
    props: {
      branchId: {
        type: String,
        required: true,
      },
      nurseryId: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        expanded: false,
        tagManager: null,
        imageManager: null,
        playbackController: new PlaybackController(),
        comparisonController: new ComparisonController(),
        displayStateManager: new DisplayStateManager(),
        timelineService: new TimelineService(),
        tags: [],
        showModal: false,
        modalType: "upload",
        successMessage: null,
        errorMessage: null,
        showDeleteImageModal: false,
        imageToDelete: null,
        isRenderingVideo: false,
        showDownloadMenu: false,
        showRenderModal: false,
        showHelpModal: false,
        renderService: null,
        renderProgress: {
          current: 0,
          total: 0,
          status: "",
          videoUrl: null,
        },
        renderSettings: {
          fps: 24,
          width: 1920,
          height: 1080,
          quality: 0.8,
        },
        activeDragHandle: null,
        isDraggingImage: false,
      };
    },
    computed: {
      selectedTag: {
        get() {
          return this.tagManager?.getSelectedTag() || null;
        },
        set(value) {
          if (this.tagManager?.setSelectedTag(value)) {
            this.handleTagChange();
          }
        },
      },
      tagOptions() {
        return this.tagManager?.getTagOptions() || [];
      },
      images() {
        return this.imageManager?.getImages() || [];
      },
      loading() {
        return this.imageManager?.isLoading() || false;
      },
      error() {
        return this.imageManager?.getError() || null;
      },
      sortedImages() {
        return ImageDisplayManager.getSortedImages(this.images, this.sortOrder);
      },
      currentImage() {
        return this.images[this.currentIndex] || null;
      },
      previousImage() {
        if (!this.comparisonMode || this.currentIndex === 0) return null;
        return this.images[this.currentIndex - 1] || null;
      },
      isAnyFileUploading() {
        return this.imageManager?.isUploading() || false;
      },
      imageDisplayState() {
        return ImageDisplayManager.getImageDisplayState(
          this.currentImage,
          this.loading,
          this.images
        );
      },
      comparisonMode: {
        get() {
          return this.comparisonController.isEnabled;
        },
        set(value) {
          this.comparisonController.isEnabled = value;
        },
      },
      comparisonValue: {
        get() {
          return this.comparisonController.value;
        },
        set(value) {
          this.comparisonController.value = value;
        },
      },
      comparisonClipStyle() {
        return this.comparisonController.getClipStyle();
      },
      comparisonLineStyle() {
        return this.comparisonController.getLineStyle();
      },
      isPlaying() {
        return this.playbackController.isPlaying;
      },
      currentIndex: {
        get() {
          return this.playbackController.currentIndex;
        },
        set(value) {
          this.playbackController.currentIndex = value;
        },
      },
      loop: {
        get() {
          return this.playbackController.loop;
        },
        set(value) {
          this.playbackController.loop = value;
        },
      },
      activeImageMenu() {
        return this.displayStateManager.getActiveImageMenu();
      },
      sortOrder() {
        return this.displayStateManager.getSortOrder();
      },
      imageAltText() {
        return ImageDisplayManager.getImageAltText(
          this.currentImage,
          this.currentIndex,
          this.images.length
        );
      },
      currentImageTimestamp() {
        return ImageDisplayManager.getImageTimestamp(this.currentImage);
      },
      previousImageTimestamp() {
        return ImageDisplayManager.getImageTimestamp(this.previousImage);
      },
      currentRangeImages() {
        return this.timelineService.getCurrentRangeImages(this.images);
      },
    },
    methods: {
      toggleExpanded() {
        this.expanded = !this.expanded;
      },
      initializeManagers() {
        this.tagManager = new TagManager(this.branchId, this.nurseryId);
        this.imageManager = new ImageManager(this.branchId, this.nurseryId);
      },
      async handleTagChange() {
        try {
          await this.imageManager.fetchImages(this.selectedTag);
          // Reset comparison mode and current index when switching tags
          this.comparisonMode = false;
          this.currentIndex = 0;
        } catch (error) {
          console.error("Error handling tag change:", error);
        }
      },
      async fetchTags() {
        try {
          await this.tagManager.fetchTags();
          this.tags = this.tagManager.tags || [];

          if (!this.selectedTag) {
            const initialTag = this.tagManager.getInitialTag();
            if (initialTag) {
              this.selectedTag = initialTag;
            }
          }
        } catch (error) {
          console.error("Error fetching tags:", error);
          this.tags = [];
        }
      },
      async deleteImage(image) {
        this.imageToDelete = image;
        this.showDeleteImageModal = true;
        this.displayStateManager.clearActiveImageMenu();
      },
      cancelDeleteImage() {
        this.showDeleteImageModal = false;
        this.imageToDelete = null;
      },
      async confirmDeleteImage() {
        try {
          await this.imageManager.deleteImage(this.imageToDelete);
          this.showDeleteImageModal = false;
          this.imageToDelete = null;
          this.showSuccessMessage("Image deleted successfully!");
        } catch (error) {
          console.error("Error deleting image:", error);
          this.showErrorMessage("Failed to delete image. Please try again.");
        }
      },
      toggleImageMenu(index) {
        this.displayStateManager.toggleImageMenu(index);
      },
      handleComparisonSliderInput(event) {
        this.comparisonValue =
          this.comparisonController.handleComparisonSliderInput(
            event,
            this.comparisonValue
          );
      },
      toggleComparisonMode() {
        const { newIndex, shouldDisable } =
          this.comparisonController.handleComparisonModeChange(
            this.currentIndex,
            this.images.length
          );

        if (newIndex !== this.currentIndex) {
          this.currentIndex = newIndex;
        }

        this.comparisonMode = this.comparisonController.toggle();

        if (shouldDisable) {
          this.comparisonMode = false;
        }
      },
      startComparisonDrag(event) {
        this.comparisonController.startDrag(event);
        this.comparisonController.setupDragListeners(
          this.startComparisonDrag,
          this.handleComparisonDrag,
          this.stopComparisonDrag
        );
      },
      handleComparisonDrag(event) {
        const container = event.target.closest("#comparison");
        if (!container) return;
        this.comparisonValue = this.comparisonController.handleDrag(
          event,
          container
        );
      },
      stopComparisonDrag() {
        this.comparisonController.stopDrag();
        this.comparisonController.removeDragListeners(
          this.handleComparisonDrag,
          this.stopComparisonDrag
        );
      },
      handleRewind() {
        this.playbackController.setPlayDirection("backward");
        this.currentIndex = this.playbackController.rewind(
          this.images,
          this.timelineService.getRangeStartDate(this.images),
          this.timelineService.getRangeEndDate(this.images)
        );
      },
      handleFastForward() {
        this.playbackController.setPlayDirection("forward");
        this.currentIndex = this.playbackController.fastForward(
          this.images,
          this.timelineService.getRangeStartDate(this.images),
          this.timelineService.getRangeEndDate(this.images)
        );
      },
      togglePlayback() {
        this.playbackController.setPlayDirection("forward");
        this.playbackController.togglePlay(
          this.images,
          (index) => {
            this.currentIndex = index;
          },
          this.timelineService.getRangeStartDate(this.images),
          this.timelineService.getRangeEndDate(this.images)
        );
      },
      playBackwards() {
        this.playbackController.setPlayDirection("backward");
        if (this.isPlaying) {
          this.stopPlayback();
        }
        this.playbackController.start(
          this.images,
          (index) => {
            this.currentIndex = index;
          },
          this.timelineService.getRangeStartDate(this.images),
          this.timelineService.getRangeEndDate(this.images)
        );
      },
      playForwards() {
        this.playbackController.setPlayDirection("forward");
        if (this.isPlaying) {
          this.stopPlayback();
        }
        this.playbackController.start(
          this.images,
          (index) => {
            this.currentIndex = index;
          },
          this.timelineService.getRangeStartDate(this.images),
          this.timelineService.getRangeEndDate(this.images)
        );
      },
      stopPlayback() {
        this.playbackController.stop();
      },
      toggleLoop() {
        this.loop = this.playbackController.toggleLoop();
      },
      updatePlaybackSpeed(speed) {
        this.playbackController.setFPS(1000 / speed);
        if (this.isPlaying) {
          this.stopPlayback();
          if (this.playbackController.playDirection === "forward") {
            this.playForwards();
          } else {
            this.playBackwards();
          }
        }
      },
      formatDate,
      handleImageClick(image) {
        const { index, shouldDisableComparison } =
          ImageDisplayManager.handleImageSelection(
            image,
            this.images,
            this.currentIndex,
            this.comparisonMode
          );

        this.currentIndex = index;
        if (shouldDisableComparison) {
          this.comparisonMode = false;
        }
      },
      toggleSortOrder() {
        const newSortOrder = this.displayStateManager.toggleSortOrder();
        this.playbackController.setSortOrder(newSortOrder);
        return newSortOrder;
      },
      getImageDisplayClass(image) {
        return ImageDisplayManager.getImageDisplayClass(
          image,
          this.currentImage
        );
      },
      cleanup() {
        this.playbackController.stop();
        this.stopComparisonDrag();
        this.displayStateManager.clearActiveImageMenu();
      },
      openUploadModal() {
        this.modalType = "upload";
        this.showModal = true;
      },
      closeModal() {
        this.showModal = false;
      },
      handleUploadSuccess() {
        this.showSuccessMessage("Image uploaded successfully!");
        this.handleTagChange();
      },
      handleImageUploaded(imageData) {
        // Instead of directly modifying images, update through the imageManager
        if (this.imageManager) {
          const currentImages = this.imageManager.getImages();
          this.imageManager.images = [imageData, ...currentImages];
        }
      },
      handleError(message) {
        this.showErrorMessage(message);
      },
      showSuccessMessage(message) {
        this.successMessage = message;
        setTimeout(() => {
          this.successMessage = null;
        }, 3000);
      },
      showErrorMessage(message) {
        this.errorMessage = message;
        setTimeout(() => {
          this.errorMessage = null;
        }, 3000);
      },
      async handleNewTag(newTag) {
        // Add the new tag to the local tags array
        this.tags.push(newTag);

        // Update the selected tag in the tag manager
        this.tagManager.setSelectedTag(newTag.id);

        // Fetch images for the new tag (this will be empty initially)
        await this.handleTagChange();
      },
      async handleDeleteTag(tagId) {
        try {
          // Use the TagManager to delete the tag and its associated images
          const result = await this.tagManager.deleteTag(tagId);

          // Clear the current images through the imageManager
          if (this.imageManager) {
            this.imageManager.images = [];
          }

          // Refresh the tags list
          await this.fetchTags();

          // If we have a next tag, select it and fetch its images
          if (result.nextTag) {
            this.selectedTag = result.nextTag;
            await this.handleTagChange();
          }

          this.showSuccessMessage(
            "Tag and associated images deleted successfully!"
          );

          // Close the upload modal if it's open
          this.closeModal();
        } catch (error) {
          console.error("Error deleting tag:", error);
          this.showErrorMessage("Failed to delete tag. Please try again.");
        }
      },
      toggleDownloadMenu() {
        this.showDownloadMenu = !this.showDownloadMenu;
      },
      async downloadCurrentImage() {
        if (!this.currentImage) return;

        try {
          this.showDownloadMenu = false;

          // Show loading state
          this.isRenderingVideo = true;

          // Fetch the source image
          const response = await fetch(this.currentImage.sourceUrl);
          const blob = await response.blob();

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = this.currentImage.originalName;
          document.body.appendChild(a);
          a.click();

          // Cleanup
          window.URL.revokeObjectURL(url);

          this.showSuccessMessage("Image downloaded successfully!");
        } catch (error) {
          console.error("Error downloading image:", error);
          this.showErrorMessage("Failed to download image. Please try again.");
        } finally {
          this.isRenderingVideo = false;
        }
      },
      openRenderModal() {
        this.showRenderModal = true;
        this.showDownloadMenu = false;
        this.renderProgress = {
          current: 0,
          total: 0,
          status: "",
          videoUrl: null,
        };
      },
      closeRenderModal() {
        if (this.renderProgress.current > 0) {
          // Cancel ongoing render
          if (this.renderService) {
            this.renderService.cancelRendering();
          }
          this.renderProgress.status = "Cancelling...";
        } else {
          this.showRenderModal = false;
          if (this.renderProgress.videoUrl) {
            URL.revokeObjectURL(this.renderProgress.videoUrl);
          }
        }
      },
      async startRender(settings) {
        if (!this.images.length) return;

        try {
          this.isRenderingVideo = true;

          // Initialize render service if needed
          if (!this.renderService) {
            this.renderService = new RenderService();
          }

          // Reset cancel flag
          this.renderService.resetCancelFlag();

          // Update settings with provided values
          this.renderSettings = { ...settings };

          // Start render with range dates
          const videoUrl = await this.renderService.renderVideo(
            this.images,
            this.renderSettings,
            (progress) => {
              this.renderProgress = progress;
            },
            this.timelineService.getRangeStartDate(this.images),
            this.timelineService.getRangeEndDate(this.images)
          );

          // Create download link
          const a = document.createElement("a");
          a.href = videoUrl;
          a.download = `timelapse-${this.formatDate(Date.now())}.mp4`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          // Cleanup
          URL.revokeObjectURL(videoUrl);

          this.showSuccessMessage(
            "Video rendered and downloaded successfully!"
          );
          this.showRenderModal = false;
        } catch (error) {
          if (error.message === "Render cancelled by user") {
            this.showSuccessMessage("Render cancelled");
          } else {
            console.error("Error rendering video:", error);
            this.showErrorMessage("Failed to render video. Please try again.");
          }
        } finally {
          this.isRenderingVideo = false;
          this.renderProgress = {
            current: 0,
            total: 0,
            status: "",
            videoUrl: null,
          };
        }
      },
      startDragging(handle, event) {
        if (this.isPlaying) {
          this.stopPlayback();
        }
        const currentValue =
          handle === "start"
            ? this.timelineService.rangeStart
            : this.timelineService.rangeEnd;
        this.timelineService.startDragging(handle, event, currentValue);
        this.activeDragHandle = handle;

        // Use passive listeners for better performance
        document.addEventListener("mousemove", this.handleDrag, {
          passive: true,
        });
        document.addEventListener("mouseup", this.stopDragging);

        // Prevent text selection while dragging
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
      },
      handleDrag(event) {
        if (!this.timelineService.isDragging) return;

        const timelineRect = this.$refs.timeline?.getBoundingClientRect();
        if (!timelineRect) return;

        const newValue = this.timelineService.handleDrag(
          event,
          timelineRect,
          this.images
        );

        if (newValue !== null) {
          if (this.timelineService.activeDragHandle === "start") {
            this.timelineService.rangeStart = newValue;
          } else {
            this.timelineService.rangeEnd = newValue;
          }
        }
      },
      stopDragging() {
        this.timelineService.stopDragging();
        this.activeDragHandle = null;
        document.removeEventListener("mousemove", this.handleDrag);
        document.removeEventListener("mouseup", this.stopDragging);

        // Restore default styles
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      },
      startImageDragging(event) {
        if (this.isPlaying) {
          this.stopPlayback();
        }
        const currentPosition = this.timelineService.getCurrentImagePosition(
          this.currentImage,
          this.images
        );
        this.timelineService.startImageDragging(event, currentPosition);
        this.isDraggingImage = true;

        // Use passive listeners for better performance
        document.addEventListener("mousemove", this.handleImageDrag, {
          passive: true,
        });
        document.addEventListener("mouseup", this.stopImageDragging);

        // Prevent text selection while dragging
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
      },
      handleImageDrag(event) {
        if (!this.timelineService.isDraggingImage) return;

        const timelineRect = this.$refs.timeline?.getBoundingClientRect();
        if (!timelineRect) return;

        const nearestTimestamp = this.timelineService.handleImageDrag(
          event,
          timelineRect,
          this.images
        );

        if (nearestTimestamp !== null) {
          const newIndex = this.images.findIndex(
            (img) => this.timelineService.getTimestamp(img) === nearestTimestamp
          );
          if (newIndex !== -1) {
            this.currentIndex = newIndex;
          }
        }
      },
      stopImageDragging() {
        this.timelineService.stopImageDragging();
        this.isDraggingImage = false;
        document.removeEventListener("mousemove", this.handleImageDrag);
        document.removeEventListener("mouseup", this.stopImageDragging);

        // Restore default styles
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      },
      handleTimelineClick(event) {
        if (this.isPlaying) {
          this.stopPlayback();
        }

        const timelineRect = this.$refs.timeline?.getBoundingClientRect();
        if (!timelineRect) return;

        const nearestTimestamp = this.timelineService.handleTimelineClick(
          event,
          timelineRect,
          this.images
        );

        if (nearestTimestamp !== null) {
          const newIndex = this.images.findIndex(
            (img) => this.timelineService.getTimestamp(img) === nearestTimestamp
          );
          if (newIndex !== -1) {
            this.currentIndex = newIndex;
          }
        }
      },
    },
    watch: {
      expanded(newValue) {
        if (newValue) {
          // Initialize managers when expanded for the first time
          this.initializeManagers();
          this.fetchTags();
        }
      },
      nurseryId: {
        immediate: true,
        handler(newValue, oldValue) {
          // Collapse the module when nurseryId changes
          if (oldValue && newValue !== oldValue) {
            this.expanded = false;
          }

          if (this.expanded) {
            this.initializeManagers();
            this.tags = [];
            this.fetchTags();
          }
        },
      },
      comparisonMode: {
        handler(newValue) {
          if (newValue && this.currentIndex === 0) {
            this.currentIndex = Math.min(1, this.images.length - 1);
          }
        },
        immediate: true,
      },
    },
    beforeDestroy() {
      this.cleanup();
      if (this.renderService) {
        this.renderService.terminate();
      }
    },
  };
</script>

<style scoped>
  .overflow-y-auto {
    scrollbar-width: thin;
    scrollbar-color: oklch(20.5% 0 0) oklch(10.5% 0 0);
    -webkit-overflow-scrolling: touch;
  }

  .overflow-y-auto::-webkit-scrollbar {
    width: 4px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: oklch(10.5% 0 0);
    border-radius: 2px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: oklch(20.5% 0 0);
    border-radius: 2px;
    opacity: 0.5;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: oklch(20.5% 0 0);
  }

  .image-menu {
    position: relative;
    z-index: 10;
  }

  #comparison {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  #comparison figure {
    position: relative;
    margin: 0;
    height: 100%;
  }

  /* Remove old comparison slider styles */

  /* Hide scrollbar for tag slider */
</style>
